import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import {
  FiGlobe, FiCheck, FiMapPin, FiEdit3, FiImage, FiSearch,
  FiUploadCloud, FiShield, FiKey, FiMonitor,
} from 'react-icons/fi';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';
import CleanVideo from '../components/CleanVideo';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-emerald/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-emerald/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-emerald" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ number, title, desc }) => (
  <div className="flex gap-6 items-start">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-emerald flex items-center justify-center text-white font-bold">
      {number}
    </div>
    <div>
      <h4 className="text-lg font-bold text-white mb-1">{title}</h4>
      <p className="text-muted text-sm">{desc}</p>
    </div>
  </div>
);

const Catalina = () => {
  const PRODUCT = getProduct('catalina');
  const STORE_LINK = PRODUCT.storeLink;

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-emerald/10 border border-accent-emerald/20 text-accent-emerald text-sm font-medium mb-6">
              <SafeIcon icon={FiGlobe} /> Websites for people who have a business to run
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
              A Real Website for Your Business<br />
              <span className="text-accent-emerald">In About Five Minutes</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed">
              Pick your trade, type your business details, and Catalina writes, designs and
              publishes a complete five page website. No accounts, no subscriptions, no code.
              Your website, on your computer, forever.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              {STORE_LINK ? (
                <MicrosoftStoreBtn
                  accentColor="bg-accent-emerald"
                  className="px-10 py-4 scale-110"
                  href={STORE_LINK}
                />
              ) : (
                <span className="inline-flex items-center gap-2 px-8 py-4 border border-accent-emerald/40 text-accent-emerald font-bold uppercase tracking-widest text-sm">
                  Coming soon to the Microsoft Store
                </span>
              )}
              <div className="flex items-center gap-2 text-sm text-muted">
                <SafeIcon icon={FiShield} className="text-accent-emerald" />
                <span>One payment: {PRODUCT.price}. No subscriptions, ever.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Everything a Local Business Needs Online</h2>
            <p className="text-muted">Five pages, written for your trade: home, about, services with real prices, gallery and contact.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={FiEdit3}
              title="104 Trades Built In"
              desc="Hairdressers, plumbers, dentists, gyms, restaurants and a hundred more. Each with its own services, prices and photography."
            />
            <FeatureCard
              icon={FiImage}
              title="AI Logo & Photos"
              desc="Generate a logo and niche photos with AI, search free stock photography, or upload your own pictures."
            />
            <FeatureCard
              icon={FiMapPin}
              title="Map & Local SEO"
              desc="Your address becomes a real map. Titles, descriptions and structured data are written for local search."
            />
            <FeatureCard
              icon={FiUploadCloud}
              title="Publish Free"
              desc="One click puts your website online for free. Or download the files and host them absolutely anywhere."
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">From Nothing to Published</h2>
            <p className="text-muted">No technical knowledge required. If you can fill in a form, you can do this.</p>
          </div>
          <div className="space-y-10">
            <Step number="1" title="Pick your trade" desc="Choose from 16 families and 104 trades, or describe your business and Catalina works out the rest." />
            <Step number="2" title="Type your details" desc="Name, city, phone, opening hours. Pick your colour and style. Catalina writes all five pages around them." />
            <Step number="3" title="Click to edit" desc="Click any text on the page and type. Click any photo to swap it. What you see is what gets published." />
            <Step number="4" title="Publish" desc="One click puts it online free. Send the files to a client, or export a developer-ready project." />
          </div>
        </div>
      </section>

      {/* Trailer */}
      <section className="py-20 bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="rule-label mb-6">Watch it work, from trade to live website</p>
          <CleanVideo src="catalina.mp4" title="Catalina AI Web Builder trailer" />
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Catalina AI Web Builder</p>
          <Reel frames={PRODUCT.frames} label="Catalina AI Web Builder" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Ownership / BYOK */}
      <section className="py-20 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Yours, Not Rented</h2>
            <p className="text-muted">The opposite of a website subscription.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={FiKey}
              title="Bring Your Own Key"
              desc="Connect your own AI key from Google AI Studio, DeepSeek or OpenRouter. Catalina walks you through getting one, step by step."
            />
            <FeatureCard
              icon={FiMonitor}
              title="Everything Stays Local"
              desc="Your websites live in your Documents folder as plain files. No cloud accounts, no telemetry, no analytics. Ever."
            />
            <FeatureCard
              icon={FiSearch}
              title="No Lock-In"
              desc="Download your site as a zip, hand it to any web designer, or export it as a developer-ready Astro project."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-white mb-6">Stop Renting Your Website.</h2>
          <p className="text-muted mb-10">
            Website builders charge every month, forever. Catalina is a Windows desktop app
            you buy once. Make one website or a hundred; they are all yours.
          </p>
          <div className="bg-card border border-border p-10 rounded-none relative overflow-hidden">
            <div className="text-6xl font-bold text-white mb-2">{PRODUCT.price}</div>
            <div className="text-muted text-sm mb-8 font-medium italic">One-time payment • No subscription • BYOK</div>

            <div className="flex flex-col items-center gap-4">
              {STORE_LINK ? (
                <MicrosoftStoreBtn
                  accentColor="bg-accent-emerald"
                  className="px-12 py-5 scale-110"
                  href={STORE_LINK}
                />
              ) : (
                <span className="inline-flex items-center gap-2 px-8 py-4 border border-accent-emerald/40 text-accent-emerald font-bold uppercase tracking-widest text-sm">
                  Coming soon to the Microsoft Store
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-muted mt-8">
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Windows 10/11</span>
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Works Without a Key Too</span>
              <span className="flex items-center gap-1"><SafeIcon icon={FiCheck} className="text-accent-emerald" /> Zero Tracking or Ads</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Catalina;
